import { useGlobal } from '@hooks/useGlobal';
import { OpensiteRoutes } from '@router/openSiteRoute';
import { ROLE } from '@router/role';
import { RoleRoute } from '@router/roleRoute';
import { appRoutes } from '@router/routes';
import { lazy, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import './App.less';
import { QueryKey } from './constants/queryKey';
import { PageProvider } from './context/Page/PageProvider';
import './i18n';
import { PrivateRoute } from './router/privateRoute';
import profileService from './services/profile.service';
import { handleMessageError } from './util/handleMessageError';

const AuthLayout = lazy(() => import('@/layout/Auth'));
const PageLayout = lazy(() => import('@/layout/Main'));
const Login = lazy(() => import('@/pages/Login'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const DashBoard = lazy(() => import('@/pages/DashBoard'));

const Profile = lazy(() => import('@/pages/Profile'));
const ProfileEdit = lazy(() => import('@/pages/Profile/Edit'));

const SystemAccountList = lazy(() => import('@/pages/SystemAccount/List'));
const SystemAccountAdd = lazy(() => import('@/pages/SystemAccount/Add'));
const SystemAccountEdit = lazy(() => import('@/pages/SystemAccount/Edit'));

const CompanyAccountList = lazy(() => import('@/pages/CompanyAccount/List'));
const CompanyAccountAdd = lazy(() => import('@/pages/CompanyAccount/Add'));
const CompanyAccountEdit = lazy(() => import('@/pages/CompanyAccount/Edit'));

const CompanyList = lazy(() => import('@/pages/Company/List'));
const CompanyAdd = lazy(() => import('@/pages/Company/Add'));
const CompanyEdit = lazy(() => import('@/pages/Company/Edit'));

const ContractList = lazy(() => import('@/pages/Contract/List'));
const ContractAdd = lazy(() => import('@/pages/Contract/Add'));
const ContractEdit = lazy(() => import('@/pages/Contract/Edit'));

const FranchiseList = lazy(() => import('@/pages/Franchise/List'));
const FranchiseAdd = lazy(() => import('@/pages/Franchise/Add'));
const FranchiseEdit = lazy(() => import('@/pages/Franchise/Edit'));

const StoreList = lazy(() => import('@/pages/Store/List'));
const StoreAdd = lazy(() => import('@/pages/Store/Add'));
const StoreEdit = lazy(() => import('@/pages/Store/Edit'));

const NotFound = lazy(() => import('@/pages/NotFound'));

type LazyLoadHOC = {
    component: React.LazyExoticComponent<any>;
};

export const LazyLoadComponent: React.FC<LazyLoadHOC> = ({ component: Component, ...props }) => {
    return (
        <PageProvider>
            <Component {...props} />
        </PageProvider>
    );
};

function App() {
    const { t } = useTranslation();
    const { isLoggedIn: isAuthenticated, setLoggedIn } = useGlobal();
    const { data: dataProfile, refetch: refetchProfile } = useQuery(QueryKey.me, profileService.get, {
        refetchOnWindowFocus: false,
        enabled: false,
        onError: (error: any) => {
            handleMessageError(error, t);
        },
    });

    useEffect(() => {
        if (isAuthenticated && localStorage.getItem('access_token')) {
            refetchProfile();
        } else {
            localStorage.removeItem('access_token');
            setLoggedIn(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    return (
        <BrowserRouter>
            <PageProvider>
                <Helmet>
                    <title>{t('common:APP_TITLE')}</title>
                </Helmet>
                <Routes>
                    <Route element={<OpensiteRoutes isAuthenticated={isAuthenticated} />}>
                        <Route element={<AuthLayout />}>
                            <Route path={`${appRoutes.AUTH.LOGIN}`} element={<Login />} />
                            <Route path={`${appRoutes.FORGOT_PASSWORD.ROOT}`} element={<ForgotPassword />} />
                            <Route path={`${appRoutes.RESET_PASSWORD.ROOT}`} element={<ResetPassword />} />
                        </Route>
                    </Route>
                    <Route element={<PrivateRoute isAuthenticated={isAuthenticated} />}>
                        <Route element={<PageLayout />}>
                            <Route element={<RoleRoute roles={[ROLE.Admin, ROLE.User]} />}>
                                <Route index element={<LazyLoadComponent component={DashBoard} />} />

                                <Route
                                    path={`${appRoutes.PROFILE.ROOT}`}
                                    element={<LazyLoadComponent component={Profile} />}
                                />
                                <Route
                                    path={`${appRoutes.PROFILE.EDIT}`}
                                    element={<LazyLoadComponent component={ProfileEdit} />}
                                />
                                <Route
                                    path={`${appRoutes.DASH_BOARD.ROOT}`}
                                    element={<LazyLoadComponent component={DashBoard} />}
                                />

                                <Route
                                    path={`${appRoutes.SYSTEM_ACCOUNT.LIST}`}
                                    element={<LazyLoadComponent component={SystemAccountList} />}
                                />
                                <Route element={<PrivateRoute isAuthenticated={!!(dataProfile?.type === 'super')} />}>
                                    <Route
                                        path={`${appRoutes.SYSTEM_ACCOUNT.ADD}`}
                                        element={<LazyLoadComponent component={SystemAccountAdd} />}
                                    />
                                    <Route
                                        path={`${appRoutes.SYSTEM_ACCOUNT.EDIT}/:id`}
                                        element={<LazyLoadComponent component={SystemAccountEdit} />}
                                    />
                                </Route>

                                <Route
                                    path={`${appRoutes.COMPANY.LIST}`}
                                    element={<LazyLoadComponent component={CompanyList} />}
                                />
                                <Route
                                    path={`${appRoutes.COMPANY.ADD}`}
                                    element={<LazyLoadComponent component={CompanyAdd} />}
                                />
                                <Route
                                    path={`${appRoutes.COMPANY.EDIT}/:id`}
                                    element={<LazyLoadComponent component={CompanyEdit} />}
                                />

                                <Route
                                    path={`${appRoutes.CONTRACT.LIST}`}
                                    element={<LazyLoadComponent component={ContractList} />}
                                />
                                <Route
                                    path={`${appRoutes.CONTRACT.ADD}`}
                                    element={<LazyLoadComponent component={ContractAdd} />}
                                />
                                <Route
                                    path={`${appRoutes.CONTRACT.EDIT}/:id`}
                                    element={<LazyLoadComponent component={ContractEdit} />}
                                />

                                <Route
                                    path={`${appRoutes.FRANCHISE.LIST}`}
                                    element={<LazyLoadComponent component={FranchiseList} />}
                                />
                                <Route
                                    path={`${appRoutes.FRANCHISE.ADD}`}
                                    element={<LazyLoadComponent component={FranchiseAdd} />}
                                />
                                <Route
                                    path={`${appRoutes.FRANCHISE.EDIT}/:id`}
                                    element={<LazyLoadComponent component={FranchiseEdit} />}
                                />

                                <Route
                                    path={`${appRoutes.STORE.LIST}`}
                                    element={<LazyLoadComponent component={StoreList} />}
                                />
                                <Route
                                    path={`${appRoutes.STORE.ADD}`}
                                    element={<LazyLoadComponent component={StoreAdd} />}
                                />
                                <Route
                                    path={`${appRoutes.STORE.EDIT}/:id`}
                                    element={<LazyLoadComponent component={StoreEdit} />}
                                />

                                <Route
                                    path={`${appRoutes.COMPANY_ACCOUNT.LIST}`}
                                    element={<LazyLoadComponent component={CompanyAccountList} />}
                                />
                                <Route
                                    path={`${appRoutes.COMPANY_ACCOUNT.ADD}`}
                                    element={<LazyLoadComponent component={CompanyAccountAdd} />}
                                />
                                <Route
                                    path={`${appRoutes.COMPANY_ACCOUNT.EDIT}/:id`}
                                    element={<LazyLoadComponent component={CompanyAccountEdit} />}
                                />
                            </Route>
                        </Route>
                    </Route>
                    {/* <Route path="test" element={<Test />} /> */}
                    {/* <Route path="table-demo" element={<TableDemo />} /> */}
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </PageProvider>
        </BrowserRouter>
    );
}

export default App;
